import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { getLiveOffer, resolveTiers } from '@theme/sd-intelligems-offer';

/**
 * SD Promotion Tracker
 *
 * A tiered cart-total progress bar ("Buy More Save More"). Listens for cart
 * updates, fetches the live cart total, and animates progress toward the live
 * offer's thresholds (theme settings style up to four). Used in three placements
 * (same element, CSS-driven):
 *   - floating: sticky/collapsible on non-product pages
 *   - inline:   in-flow (product page section / theme block), no collapse
 *   - docked:   collapsible band on top of the mobile PDP sticky CTA
 *
 * Tiers come from the live Intelligems offer (single source of truth):
 * `window.igData.offers.getAll()[0].offer.tiers`, resolved to the shopper's A/B
 * variant. Markers are built CLIENT-side here (the server can't know the
 * variant-specific tiers). Per-tier presentation (icons, marker color) comes
 * from Theme settings, keyed by tier index, via `data-tier-icons`.
 *
 * Intelligems doesn't run in the theme-editor iframe, so in the editor
 * (`Shopify.designMode`) a built-in sample (`sampleTiers`) is rendered instead
 * so the component stays visible and stylable. On the storefront, if no offer
 * resolves the bar hides — placeholder numbers are never shown to a shopper.
 *
 * @extends {Component}
 */
class SdPromotionTracker extends Component {
  #abortController = null;

  // True while the collapsible panel is mid-transition. The labels are absolutely
  // positioned at their full-height spots from the first frame, while the panel is
  // still short, so a measurement taken then reads a huge shortfall (measured 478px
  // of reserve on a 145px panel) and the card visibly balloons before a later pass
  // corrects it. See #settlePanel.
  #panelAnimating = false;

  #panelSettleTimer = null;

  /** @type {((event: TransitionEvent) => void) | null} */
  #onPanelTransition = null;

  // Tracks whether we've already logged the "no data source" notice, so it fires
  // at most once per empty episode rather than on every re-resolve.
  #emptyLogged = false;

  // Standard cart events fire before the mutation; the promise settles once the
  // cart has actually changed, which is when the total is worth re-reading.
  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    event.promise?.then(() => this.#refresh()).catch(() => {});
  };

  // Intelligems can load after this component mounts; re-resolve tiers + markers
  // and repaint once its data is ready.
  #onIgReady = () => {
    this.tiers = this.#getTiers();
    this.#syncMarkers();
    // Don't reveal here: #refresh() -> #render() reveals once the cart total is
    // painted, so the bar appears fully populated (markers AND feedback) in a
    // single frame rather than flashing markers with blank feedback for a fetch RTT.
    this.#refresh();
  };

  // Wired via on:click="/handleToggle" on the toggle button (Component framework).
  handleToggle() {
    const collapsed = !this.classList.contains('sd-promotion-tracker--collapsed');
    this.#setCollapsed(collapsed, true);
  }

  // Wired via on:click="/handleClose"; minimizes the desktop bar to a compact
  // "Buy More Save More" pill (re-openable via the pill) rather than dismissing it.
  handleClose() {
    this.#setMinimized(true, true);
    // The Close button just went display:none — move focus to the pill that
    // replaced it so keyboard focus isn't dropped to <body>.
    this.querySelector('.sd-promotion-tracker__pill')?.focus();
  }

  // Wired via on:click="/handleExpand" on the minimized pill; restores the full bar.
  handleExpand() {
    this.#setMinimized(false, true);
    // The pill just went display:none — hand focus back to the Close control.
    this.querySelector('.sd-promotion-tracker__close')?.focus();
  }

  // Crossing the floating breakpoint re-evaluates `inert`: at desktop the panel is
  // display:contents (visible), so a persisted collapsed state must not leave it inert.
  #onBreakpointChange = () => {
    // The leading line differs per breakpoint, so reposition the markers + fill.
    this.#syncMarkers();
    // Only repaint when a real cart total has already landed. Rendering a
    // fabricated 0 here would satisfy #updateVisibility's hasContent gate and
    // reveal the bar with fake zero-progress content before the first /cart.js
    // response arrives. While still hidden the fill position is invisible anyway;
    // the pending #refresh() will paint (and reveal) it correctly.
    if (this.lastTotal != null) this.#render(this.lastTotal);
    // Floating-only: re-evaluate the collapsed panel's inert (see #setCollapsed).
    if (this.refs.panel instanceof HTMLElement) {
      const collapsed = this.classList.contains('sd-promotion-tracker--collapsed');
      this.refs.panel.toggleAttribute('inert', collapsed && !this.desktopMql?.matches);
    }
  };

  // Fade the floating bar out as the footer scrolls into view so it doesn't sit
  // behind it (mirrors the sticky-CTA footer-observer pattern).
  #setupFooterFade() {
    const footer = document.querySelector('.sd-footer-section');
    if (!footer) return;
    this.footerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.classList.toggle('sd-promotion-tracker--near-footer', entry.isIntersecting);
        }
      },
      { root: null, rootMargin: '0px 0px 100px 0px', threshold: 0 }
    );
    this.footerObserver.observe(footer);
  }

  connectedCallback() {
    super.connectedCallback();

    // Intelligems doesn't run in the theme-editor iframe, so in the editor we
    // render a built-in sample to keep the component visible/stylable. Production
    // always uses the live Intelligems offer (the only data source).
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.currency = this.dataset.currency || 'USD';
    this.locale = this.dataset.locale || 'en-US';
    // Marker spacing: 'equidistant' = even gaps (Figma); 'accurate' = placed by
    // real value. Fill + bubble follow the same scale (see #posPct).
    this.spacing = this.dataset.spacing || 'equidistant';
    // Leading-line length (% of track before the first tier circle), independent
    // per breakpoint. Used in #posPct via #currentLead(); repositioned on resize.
    this.leadDesktop = Number(this.dataset.leadDesktop);
    if (!Number.isFinite(this.leadDesktop)) this.leadDesktop = 30;
    this.leadMobile = Number(this.dataset.leadMobile);
    if (!Number.isFinite(this.leadMobile)) this.leadMobile = 15;
    // The drawer is far narrower than the page, so it gets its own value rather than
    // inheriting the site-wide one (WEB-366).
    this.leadCart = Number(this.dataset.leadCart);
    if (!Number.isFinite(this.leadCart)) this.leadCart = 30;
    // Track the mobile/desktop breakpoint so the leading (and, when floating, the
    // collapsed-panel inert) react to it; repositions markers + fill on change.
    this.desktopMql = window.matchMedia('(min-width: 750px)');
    this.desktopMql.addEventListener('change', this.#onBreakpointChange);
    // The inline layout is container-query driven (PDP column, cart drawer), so its
    // width changes without a breakpoint change — re-clamp the end labels when it does.
    this.resizeObserver = new ResizeObserver(() => this.#clampLabels());
    this.resizeObserver.observe(this);
    // Copy templates are merchant-configurable (theme settings) so the wording
    // can be A/B-tested without a deploy. {reward} = tier label, {amount} = remaining.
    this.msgUnlocked = this.dataset.msgUnlocked || '{reward} unlocked!';
    this.msgNext = this.dataset.msgNext || 'Add {amount} for {reward}.';
    this.msgStart = this.dataset.msgStart || 'Unlock your first discount!';
    // Current-cart-amount tooltip is opt-in (default off): it can overlap the tier labels.
    this.showCartAmount = this.dataset.showCartAmount === 'true';
    // Customizable label above each tier icon; {amount} is filled from the resolved tiers.
    this.msgThreshold = this.dataset.msgThreshold || 'Spend {amount}';
    // Item-count copy for quantity-based (unitType: 'unit') Intelligems offers,
    // from the localized `general.item_count` plural forms. The snippet fills the
    // count slot with the %COUNT% sentinel, which #count() swaps for the quantity.
    this.msgItemOne = this.dataset.msgItemOne || '%COUNT% item';
    this.msgItemOther = this.dataset.msgItemOther || '%COUNT% items';
    // Progress basis: 'dollar' = cart subtotal in cents (default), 'unit' = cart
    // item count. Resolved per-offer in #getTiers.
    this.unitType = 'dollar';
    // Per-index presentation for Intelligems-mode markers (icons + color).
    this.tierIcons = this.#parseTierIcons();

    this.tiers = this.#getTiers();
    // Build the markers + summary from the resolved tiers (Intelligems offer on
    // the storefront, or the built-in sample in the editor).
    this.#syncMarkers();
    // Hide the whole bar until tiers resolve, so a disabled/absent offer doesn't
    // leave an empty "Buy More Save More" shell sitewide. Defer the "no data
    // source" notice while Intelligems is still loading — ig:ready re-runs this.
    const igPending = !this.designMode && typeof window.igData?.offers?.getAll !== 'function';
    this.#updateVisibility(!igPending);

    // Collapse is floating + docked. Restore the saved state so collapsing and
    // navigating preserves the shopper's choice (browsing state preserved).
    if (this.dataset.placement === 'docked') {
      // Own key, and collapsed until the shopper opens it (WEB-395), so the PDP
      // default doesn't follow the site-wide bar's expanded default.
      let collapsed = true;
      try {
        collapsed = localStorage.getItem(this.#collapsedKey) !== '0';
      } catch {
        // localStorage can throw (private mode) — keep the collapsed default.
      }
      this.#setCollapsed(collapsed, false);
    }

    if (this.dataset.placement === 'floating') {
      // Restore the minimized (compact pill) state so minimizing and navigating
      // preserves the shopper's choice. Visual on desktop only (see CSS); harmless
      // on mobile, which keeps its own full-width collapse.
      let minimized = false;
      try {
        minimized = localStorage.getItem(SdPromotionTracker.minimizedKey) === '1';
      } catch {
        // localStorage can throw (private mode) — default to expanded.
      }
      this.#setMinimized(minimized, false);

      let collapsed = false;
      try {
        collapsed = localStorage.getItem(SdPromotionTracker.storageKey) === '1';
      } catch {
        // localStorage can throw when disabled/blocked (e.g. Safari private mode).
        // Persistence is non-essential — default to expanded.
      }
      // Desktop floating has no collapse UI (the panel is display:contents); the
      // breakpoint watcher set up above scopes the collapsed-state `inert` to where
      // the panel is actually hidden and re-evaluates it on resize.
      this.#setCollapsed(collapsed, false);
      // Fade the bar out as the footer scrolls into view so it never sits behind it.
      requestAnimationFrame(() => this.#setupFooterFade());
    }

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);

    if (this.designMode) {
      // Editor preview: paint the sample at a representative mid-state; skip cart
      // fetching and Intelligems wiring (neither runs inside the editor iframe).
      this.#render(SdPromotionTracker.sampleTotal);
    } else {
      window.addEventListener('ig:ready', this.#onIgReady);
      this.#refresh();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
    window.removeEventListener('ig:ready', this.#onIgReady);
    this.desktopMql?.removeEventListener('change', this.#onBreakpointChange);
    this.resizeObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.#stopPanelWatch();
    this.#panelAnimating = false;
  }

  /**
   * Resolve the active tiers as `[{ value, label, max }]` ascending, and set
   * `this.unitType` to the basis those values are measured in:
   *   - 'dollar' → `value` is cart spend in cents
   *   - 'unit'   → `value` is a cart item count
   * Reads the variant-resolved Intelligems offer (either basis); the theme
   * editor preview uses the built-in dollar-based sample.
   *
   * The offer reading itself lives in `@theme/sd-intelligems-offer`, shared with the
   * promo badge (WEB-387) so the two can never disagree on tier wording.
   */
  #getTiers() {
    // Editor preview: Intelligems isn't available in the editor iframe, so use
    // the built-in sample so the component stays visible and stylable.
    if (this.designMode) {
      this.unitType = 'dollar';
      return SdPromotionTracker.sampleTiers;
    }
    // Intelligems not loaded yet on the storefront, or present with no usable offer for
    // this shopper: either way there is nothing to ladder against. Hide until ig:ready
    // fires (never show placeholder numbers to a shopper).
    const offer = getLiveOffer(this.dataset.offerId)?.offer;
    const { tiers, unitType } = resolveTiers(offer);
    this.unitType = unitType;
    return tiers;
  }

  #parseTierIcons() {
    try {
      const icons = JSON.parse(this.dataset.tierIcons || '[]');
      return Array.isArray(icons) ? icons : [];
    } catch {
      return [];
    }
  }

  /**
   * Build the markers from the resolved tiers (the live Intelligems offer on the
   * storefront, or the editor sample). Safe to call again when the offer resolves.
   */
  #syncMarkers() {
    const track = this.querySelector('.sd-promotion-tracker__track');
    if (!track) return;

    for (const marker of track.querySelectorAll('.sd-promotion-tracker__marker')) {
      marker.remove();
    }

    const tiers = this.tiers;
    if (tiers.length === 0) return;

    // With no leading line the first marker sits at 0%, i.e. straddling the start of
    // the rail. Surfaces that run their rail flush to the content edge (the cart
    // drawer band) need to reserve room for it; flag the case for CSS.
    this.classList.toggle('sd-promotion-tracker--no-lead', this.spacing === 'equidistant' && this.#currentLead() <= 0);

    const fragment = document.createDocumentFragment();

    for (const [index, tier] of tiers.entries()) {
      const icons = this.tierIcons[index] || {};
      const pos = this.#posPct(tier.value);

      const marker = document.createElement('div');
      marker.className = 'sd-promotion-tracker__marker';
      marker.dataset.value = String(tier.value);
      marker.style.left = `${pos}%`;
      if (icons.color) marker.style.setProperty('--sd-promo-marker-color', icons.color);

      const threshold = document.createElement('span');
      threshold.className = 'sd-promotion-tracker__marker-threshold';
      threshold.textContent = this.#fill(this.msgThreshold, { amount: this.#formatValue(tier.value) });

      const iconWrap = document.createElement('span');
      iconWrap.className = 'sd-promotion-tracker__marker-icon';
      if (icons.on) iconWrap.appendChild(this.#icon(icons.on, 'sd-promotion-tracker__icon--on'));
      if (icons.off) iconWrap.appendChild(this.#icon(icons.off, 'sd-promotion-tracker__icon--off'));
      // Default glyph for an unconfigured inactive icon — matches the Figma's "%".
      if (!icons.off) {
        const glyph = document.createElement('span');
        glyph.className = 'sd-promotion-tracker__marker-glyph';
        glyph.textContent = '%';
        glyph.setAttribute('aria-hidden', 'true');
        iconWrap.appendChild(glyph);
      }
      const check = document.createElement('span');
      check.className = 'sd-promotion-tracker__marker-check';
      check.setAttribute('aria-hidden', 'true');
      iconWrap.appendChild(check);

      marker.append(threshold, iconWrap);

      if (tier.label) {
        const label = document.createElement('span');
        label.className = 'sd-promotion-tracker__marker-label';
        label.textContent = tier.label;
        marker.appendChild(label);
      }

      fragment.appendChild(marker);
    }

    track.appendChild(fragment);

    this.#clampLabels();
  }

  /**
   * Keep the end markers' text inside the card. Each threshold/label is centred on
   * its marker, so a wide one ("Free Shipping") on a marker near either end of the
   * rail overhangs the tracker's padding. Worst on the narrow inline layout, where
   * the rail is deliberately pulled out to the content edges (WEB-366).
   *
   * Widths are only knowable after layout, so the nudge is measured rather than
   * expressed in CSS: reset to the centred transform, then shift back inside only
   * what actually overhangs. Everything with room keeps the plain centred rule.
   */
  #clampLabels() {
    const thresholds = [...this.querySelectorAll('.sd-promotion-tracker__marker-threshold')];
    const labels = [...this.querySelectorAll('.sd-promotion-tracker__marker-label')];
    if (thresholds.length === 0 && labels.length === 0) return;

    for (const node of [...thresholds, ...labels]) node.style.transform = '';

    const styles = getComputedStyle(this);
    const box = this.getBoundingClientRect();
    const contentMin = box.left + (parseFloat(styles.paddingLeft) || 0);
    const contentMax = box.right - (parseFloat(styles.paddingRight) || 0);

    this.#fitLabels(labels, contentMin, contentMax);

    // Thresholds sit inside the padding with a small gutter so they don't read as
    // clipped. Labels are already sized to fit, so they only need the padding itself.
    this.#nudgeInside(
      thresholds,
      contentMin + SdPromotionTracker.labelGutter,
      contentMax - SdPromotionTracker.labelGutter
    );
    this.#nudgeInside(labels, contentMin, contentMax);

    this.#reserveLabelRoom(labels);
    this.#settleLabels();
  }

  /**
   * Labels are absolutely positioned, so a wrapped one doesn't grow the card and would
   * spill past its bottom edge. Reserve exactly what overflows.
   *
   * Measured twice on purpose: the slot cap applied above can rewrap a label, and the
   * first read lands before that settles — leaving an overflow with no reserve set,
   * which is also no size change, so the ResizeObserver never comes back to fix it.
   * The second pass costs one layout on a handful of nodes and converges.
   */
  #reserveLabelRoom(labels) {
    if (labels.length === 0) return;
    // Mid-expand the panel is still clipped, so the gap below the lowest label is
    // meaningless — #settlePanel re-runs this once the transition ends.
    if (this.#panelAnimating) return;
    // Collapsed, the card is clipped to its header while the labels keep laying out at
    // full height below it, so every gap reads as a large shortfall and the reserve
    // grows without bound (measured 143px on a collapsed mobile card). Leave the value
    // alone; expanding resizes the card, which brings the observer back here.
    if (this.classList.contains('sd-promotion-tracker--collapsed')) return;
    const lowest = labels.reduce((max, node) => Math.max(max, node.getBoundingClientRect().bottom), 0);
    if (!lowest) return;

    // Aim for a set amount of breathing room under the lowest label rather than merely
    // avoiding a clip: a wrapped pill that cleared the card's edge by 3px was inside it
    // but read as crammed against the bottom (WEB-371).
    const gap = this.getBoundingClientRect().bottom - lowest;
    const current = parseFloat(this.style.getPropertyValue('--sd-promo-label-reserve')) || 0;
    // Correcting the CURRENT reserve by the shortfall converges: once the gap matches the
    // target the correction is zero, so the value holds instead of being dropped and
    // re-added on every pass.
    const next = Math.max(0, Math.round(current + SdPromotionTracker.labelBottomGap - gap));

    if (next === 0) this.style.removeProperty('--sd-promo-label-reserve');
    else this.style.setProperty('--sd-promo-label-reserve', `${next}px`);
  }

  /**
   * Re-measure once on the next frame. The drawer opens by transforming its panel, so
   * the tracker's own box never changes size and the ResizeObserver never fires — yet
   * the geometry it was measured against was mid-open. One settle pass after paint
   * corrects that without polling; the flag keeps it to a single extra pass.
   */
  #settleLabels() {
    if (this.settlingLabels) {
      this.settlingLabels = false;
      return;
    }
    this.settlingLabels = true;
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#clampLabels();
      this.settlingLabels = false;
    });
  }

  /**
   * Give each label the room its neighbours genuinely don't need, so a merchant's
   * discount name only wraps when it truly cannot fit (WEB-371).
   *
   * Every label gets an equal share of the marker spacing, plus whatever its
   * neighbours leave unused — most tiers are short ("20% Off"), so the long one
   * beside them usually fits on one line at full size. Slack is measured from the
   * neighbours' natural widths rather than first-come-first-served, otherwise a long
   * first label eats its neighbour's room and pushes THAT one into a wrap.
   *
   * The end labels are additionally bounded by the card's content box: they may sit
   * off-centre from their marker (the nudge below does that) rather than shrink, which
   * is what lets a name like "Free Shipping" stay on one line with the padding intact.
   */
  #fitLabels(labels, contentMin, contentMax) {
    const markers = [...this.querySelectorAll('.sd-promotion-tracker__marker')];
    if (markers.length < 2 || markers.length !== labels.length) {
      for (const label of labels) {
        label.style.maxWidth = '';
        label.style.whiteSpace = '';
      }
      return;
    }

    const gutter = SdPromotionTracker.labelGutter;
    const centres = markers.map((marker) => {
      const rect = marker.getBoundingClientRect();
      return rect.left + rect.width / 2;
    });
    // Natural width = what the label wants on one line at its own size.
    const natural = labels.map((label) => {
      label.style.maxWidth = 'none';
      label.style.whiteSpace = 'nowrap';
      return label.getBoundingClientRect().width;
    });
    const fair = Math.max(0, centres[1] - centres[0] - gutter);
    const last = labels.length - 1;

    labels.forEach((label, i) => {
      const slackBefore = i === 0 ? 0 : Math.max(0, fair - natural[i - 1]);
      const slackAfter = i === last ? 0 : Math.max(0, fair - natural[i + 1]);
      let cap = fair + slackBefore + slackAfter;
      if (i === 0) cap = Math.min(cap, centres[1] - natural[1] / 2 - gutter - contentMin);
      if (i === last) cap = Math.min(cap, contentMax - (centres[last - 1] + natural[last - 1] / 2 + gutter));
      cap = Math.max(0, Math.min(cap, contentMax - contentMin));

      label.style.maxWidth = `${Math.round(cap)}px`;
      label.style.whiteSpace = natural[i] <= cap ? 'nowrap' : 'normal';
    });
  }

  /** Shift only what overhangs [min, max] back inside; the rest keeps the centred rule. */
  #nudgeInside(nodes, min, max) {
    for (const node of nodes) {
      const rect = node.getBoundingClientRect();
      // Compact mode hides these (zero-sized); nothing to clamp.
      if (rect.width === 0) continue;
      let shift = 0;
      if (rect.left < min) shift = min - rect.left;
      else if (rect.right > max) shift = max - rect.right;
      if (shift !== 0) node.style.transform = `translateX(calc(-50% + ${Math.round(shift)}px))`;
    }
  }

  #icon(src, modifier) {
    const img = document.createElement('img');
    img.className = `sd-promotion-tracker__icon ${modifier}`;
    img.src = src;
    img.alt = '';
    img.width = 48;
    img.height = 48;
    img.loading = 'lazy';
    return img;
  }

  /**
   * Toggle the bar's visibility. It reveals only once tiers resolve AND there is
   * content to paint (the editor sample or the first live cart total), and stays
   * hidden otherwise — so it never renders as an empty shell (offer absent, a basis
   * we can't track, or the data simply not arrived yet). Logs an info-level notice
   * once per empty episode to make "enabled but nothing shows" debuggable.
   *
   * @param {boolean} [allowLog] - Suppress the notice while Intelligems is still
   *   loading (we re-run from `ig:ready`); avoids a false "no data source" alarm
   *   during the normal startup window.
   */
  #updateVisibility(allowLog = true) {
    // Reveal only once we have resolved tiers AND something to paint in them — the
    // editor sample, or the first live cart total (this.lastTotal). This keeps the
    // empty shell hidden both before Intelligems resolves and during the gap before
    // the first /cart.js render lands, so the shopper only ever sees a fully
    // populated bar appear (never an empty one that fills in). The server markup
    // ships with --empty, so this holds even before the JS runs / if it never does.
    const hasTiers = this.tiers.length > 0;
    const hasContent = this.designMode || this.lastTotal != null;
    this.classList.toggle('sd-promotion-tracker--empty', !(hasTiers && hasContent));
    // Intelligems has loaded and has no offer for this shopper, so no reveal is coming:
    // lets the cart drawer drop its loading skeleton now instead of after its timeout.
    const igReady = typeof window.igData?.offers?.getAll === 'function';
    this.classList.toggle('sd-promotion-tracker--no-offer', !this.designMode && igReady && !hasTiers);

    // Log (once per empty episode) only when there is genuinely no tier data — NOT
    // during the normal gap where tiers resolved but the cart total is still in
    // flight, which isn't an error.
    if (!hasTiers && allowLog && !this.#emptyLogged) {
      // eslint-disable-next-line no-console
      console.info(
        'sd-promotion-tracker: enabled but no Intelligems tier data resolved — hiding. ' +
          'Usually means no active/enabled offer for this shopper (test off, or not bucketed into a variant), ' +
          'or Intelligems has not loaded on this page.'
      );
      this.#emptyLogged = true;
    } else if (hasTiers) {
      this.#emptyLogged = false;
    }
  }

  get #collapsedKey() {
    return this.dataset.placement === 'docked'
      ? SdPromotionTracker.dockedCollapsedKey
      : SdPromotionTracker.storageKey;
  }

  /**
   * Hold off label measuring until the panel's max-height transition has finished,
   * then take one clean pass. The timeout is the fallback for the cases where no
   * transitionend arrives at all: reduced motion, a display:contents panel (desktop
   * floating), or a state set before the element is painted.
   */
  #settlePanel() {
    const panel = this.refs.panel;
    if (!(panel instanceof HTMLElement)) return;

    // A toggle mid-transition cancels the running one, so drop the pending wait before
    // starting another: otherwise a fast open/close/open leaves listeners behind.
    this.#stopPanelWatch();
    this.#panelAnimating = true;

    const settle = () => {
      this.#stopPanelWatch();
      this.#panelAnimating = false;
      this.#clampLabels();
    };
    // transitioncancel fires instead of transitionend when a toggle interrupts the
    // animation; without it the flag would stay set until the fallback timeout.
    this.#onPanelTransition = (event) => {
      if (event.target === panel && event.propertyName === 'max-height') settle();
    };

    panel.addEventListener('transitionend', this.#onPanelTransition);
    panel.addEventListener('transitioncancel', this.#onPanelTransition);
    this.#panelSettleTimer = setTimeout(settle, 600);
  }

  /** Drops the pending settle listeners and timer, leaving the flag alone. */
  #stopPanelWatch() {
    clearTimeout(this.#panelSettleTimer);
    const panel = this.refs.panel;
    if (panel instanceof HTMLElement && this.#onPanelTransition) {
      panel.removeEventListener('transitionend', this.#onPanelTransition);
      panel.removeEventListener('transitioncancel', this.#onPanelTransition);
    }
    this.#onPanelTransition = null;
  }

  #setCollapsed(collapsed, persist) {
    const changed = this.classList.contains('sd-promotion-tracker--collapsed') !== collapsed;
    this.classList.toggle('sd-promotion-tracker--collapsed', collapsed);
    // Expanding animates the panel open; measure only once it has settled.
    if (changed && !collapsed) this.#settlePanel();
    if (this.refs.toggle instanceof HTMLElement) {
      this.refs.toggle.setAttribute('aria-expanded', String(!collapsed));
    }
    // The collapsed panel is only visually hidden (max-height/opacity), which
    // leaves its content — including the aria-live feedback — in the
    // accessibility tree. `inert` removes it from AT and focus while collapsed.
    if (this.refs.panel instanceof HTMLElement) {
      // At the desktop floating breakpoint the panel is `display: contents` (no
      // collapse UI), so its children stay visible — applying `inert` there would
      // make a visible track + aria-live region AT-inaccessible with no way to
      // recover. Only mark it inert where the panel is actually hidden.
      const desktopFloating = this.dataset.placement === 'floating' && this.desktopMql?.matches;
      this.refs.panel.toggleAttribute('inert', collapsed && !desktopFloating);
    }
    if (persist) {
      try {
        localStorage.setItem(this.#collapsedKey, collapsed ? '1' : '0');
      } catch {
        // Best-effort persistence — ignore storage failures (disabled/quota).
      }
    }
  }

  // Minimize (desktop) collapses the whole bar to the compact "Buy More Save More"
  // pill; expand restores it. Separate from #setCollapsed (mobile panel collapse) —
  // the two placements have independent minimize UIs and persisted state.
  #setMinimized(minimized, persist) {
    this.classList.toggle('sd-promotion-tracker--minimized', minimized);
    if (persist) {
      try {
        localStorage.setItem(SdPromotionTracker.minimizedKey, minimized ? '1' : '0');
      } catch {
        // Best-effort persistence — ignore storage failures (disabled/quota).
      }
    }
  }

  async #refresh() {
    // Cancel any in-flight request so rapid cart changes can't render stale totals.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    let total = 0;
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
      const cart = await response.json();
      if (this.unitType === 'unit') {
        // Quantity-based tiers qualify on the number of items in the cart.
        total = cart.item_count || 0;
      } else {
        // Spend-based tiers: compare against the spend BEFORE the promo discount.
        // Intelligems applies "Buy More Save More" as a LINE-LEVEL discount
        // (verified on live carts: each item carries line_level_discount_allocations,
        // cart_level_discount_applications is empty), so BOTH items_subtotal_price
        // and total_price drop once a tier unlocks — the bar would regress below
        // the threshold the shopper just crossed. original_total_price is the
        // pre-discount merchandise total: it stays stable and matches the gross
        // spend Intelligems itself qualifies tiers on.
        total = cart.original_total_price || 0;
      }
    } catch {
      // A newer request aborted this one — let that one render; don't repaint stale data.
      if (controller.signal.aborted) return;
      // Genuine network/parse failure: fall back to the last painted total (or 0 on
      // first load) so the bar still reveals instead of staying hidden forever
      // waiting on a total that never arrives.
      total = this.lastTotal ?? 0;
    }
    this.#render(total);
  }

  // Map a value to a track position %, honoring the spacing mode:
  //  - 'accurate'    linear on the real value scale
  //  - 'equidistant' (default) a leading stub (per-breakpoint setting), equal gaps
  //                  between markers, and the last tier at 100% (endpoint, no
  //                  trailing rail)
  // Fill + bubble ride the same scale in every mode. Any value other than
  // 'accurate' uses the equidistant layout (so a legacy saved value still works).
  #posPct(value) {
    const tiers = this.tiers;
    if (!tiers || tiers.length === 0) return 0;
    const last = tiers[tiers.length - 1].value;
    if (!last) return 0;
    const n = tiers.length;

    // Accurate: linear on the real value scale.
    if (this.spacing === 'accurate') {
      return Math.max(0, Math.min(100, (value / last) * 100));
    }

    // Equidistant: a short leading stub, evenly-spaced markers, last at 100%.
    // Anchors: value 0 -> 0, tier[k] -> lead + k*gap, last -> 100%.
    if (value <= 0) return 0;
    if (value >= last) return 100;
    const lead = this.#currentLead();
    const gap = n > 1 ? (100 - lead) / (n - 1) : 0;
    let prevVal = 0;
    let prevPos = 0;
    for (let k = 0; k < n; k++) {
      const tv = tiers[k].value;
      const pos = n > 1 ? lead + k * gap : 100;
      if (value <= tv) {
        const frac = tv > prevVal ? (value - prevVal) / (tv - prevVal) : 1;
        return prevPos + frac * (pos - prevPos);
      }
      prevVal = tv;
      prevPos = pos;
    }
    return 100;
  }

  // The leading-line length (% of track). The cart drawer uses its own setting at
  // every screen size — it is a narrow, fixed-width container, so the page's value
  // (which a merchant may drop to 0% to spread the site-wide bar) would crowd the
  // first marker's label against the drawer edge. Its 30% default matches the cart
  // design (Figma 5166-8486), which places the first circle at ~29%. Drawer-only:
  // .sd-cart-drawer never exists on the floating/PDP surfaces.
  #currentLead() {
    if (this.closest('.sd-cart-drawer')) return this.leadCart;
    return this.desktopMql?.matches ? this.leadDesktop : this.leadMobile;
  }

  #render(total) {
    // Remembered so a breakpoint change can reposition the fill without re-fetching.
    this.lastTotal = total;
    // A real total has now been painted; with tiers resolved this is the first
    // fully populated frame, so the bar can reveal (all synchronous in this task —
    // the reveal + marker/feedback paint below land in the same frame, no flash).
    this.#updateVisibility();
    const tiers = this.tiers;
    if (tiers.length === 0) return;

    const pct = this.#posPct(total);
    // Empty cart: no progress to show. Hide the fill (CSS) + drop the tooltip so
    // there's no orange sliver or "$0" bubble jammed at the very start of the bar.
    const empty = total <= 0;
    this.classList.toggle('sd-promotion-tracker--cart-empty', empty);

    // Fill + bubble animate via CSS custom properties (smooth transition, not a jump).
    this.style.setProperty('--sd-promo-progress', `${pct}%`);

    if (this.refs.bubble instanceof HTMLElement) {
      // Drop the tooltip when it's toggled off (default), when every tier is
      // unlocked, or at an empty cart (CSS :empty hides the empty span).
      const last = tiers[tiers.length - 1].value;
      if (!this.showCartAmount || total >= last || empty) {
        this.refs.bubble.textContent = '';
      } else {
        this.refs.bubble.textContent = this.#formatValue(total);
        this.refs.bubble.style.setProperty('--sd-promo-bubble-pos', `${pct}%`);
      }
    }

    for (const marker of this.#markers()) {
      const value = Number(marker.dataset.value || 0);
      marker.classList.toggle('is-achieved', total >= value);
    }

    if (this.refs.feedback instanceof HTMLElement) {
      const { headline, detail } = this.#feedback(total);
      const h = this.refs.feedback.querySelector('.sd-promotion-tracker__feedback-headline');
      const d = this.refs.feedback.querySelector('.sd-promotion-tracker__feedback-detail');
      if (h) h.textContent = headline;
      if (d) d.textContent = detail;
    }

    // Reaching a tier turns its label into a padded pill, which widens it after the
    // markers were built — and an absolutely-positioned label growing doesn't resize
    // this element, so the ResizeObserver never sees it. Re-clamp here or a label
    // sitting flush against the edge overhangs the moment its tier is achieved.
    // This is also the first clamp on a *visible* bar: #syncMarkers runs while it is
    // still hidden (zero-sized, nothing measurable), and #updateVisibility above has
    // just revealed it.
    this.#clampLabels();
  }

  #feedback(total) {
    const tiers = this.tiers;

    let achieved = -1;
    for (const [index, tier] of tiers.entries()) {
      if (total >= tier.value) achieved = index;
    }

    // All tiers unlocked — headline only (e.g. "30% Off unlocked!").
    if (achieved === tiers.length - 1) {
      return { headline: this.#fill(this.msgUnlocked, { reward: tiers[achieved].label }), detail: '' };
    }

    const next = tiers[achieved + 1];
    const detail = this.#fill(this.msgNext, {
      amount: this.#formatValue(next.value - total),
      reward: next.label,
    });

    // Below the first tier — "start" headline + the next-tier prompt as detail.
    if (achieved < 0) return { headline: this.msgStart, detail };

    // Mid-ladder — confirm the unlocked tier (headline) + prompt the next (detail).
    return { headline: this.#fill(this.msgUnlocked, { reward: tiers[achieved].label }), detail };
  }

  #fill(template, values) {
    return template.replace(/\{(reward|amount)\}/g, (_match, key) => values[key] ?? '');
  }

  // Format a tier/cart value for display in the active basis: money for spend-
  // based offers, an item count for quantity-based ones.
  #formatValue(value) {
    return this.unitType === 'unit' ? this.#count(value) : this.#money(value);
  }

  #money(amount) {
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        // Show cents only when present: whole tier values stay compact ("$125"),
        // but the live cart total and remaining amount show exact cents
        // ("$139.98", "$35.02" like Intelligems) instead of rounding up to "$140"
        // and overstating the shopper's progress.
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount / 100);
    } catch {
      return `$${(amount / 100).toFixed(2)}`;
    }
  }

  // Render an item count (e.g. "1 item" / "3 items") from the localized plural
  // templates, with the locale-formatted number swapped into the %COUNT% slot.
  #count(count) {
    const num = Math.max(0, Math.round(count));
    const template = num === 1 ? this.msgItemOne : this.msgItemOther;
    let formatted = String(num);
    try {
      formatted = new Intl.NumberFormat(this.locale).format(num);
    } catch {
      // Fall back to the plain integer string.
    }
    return template.replace(/%COUNT%/g, formatted);
  }

  // Markers are built client-side; query the DOM so we never miss a tier (this
  // also sidesteps scalar-vs-array ref pitfalls).
  #markers() {
    return Array.from(this.querySelectorAll('.sd-promotion-tracker__marker'));
  }

  // Built-in sample for the theme-editor preview (Intelligems doesn't run there).
  // Four tiers — a free-shipping threshold plus three percentage tiers — so the
  // preview matches the shape merchants configure icons and colours for (WEB-366).
  static sampleTiers = [
    { value: 9900, label: 'Free shipping' },
    { value: 15000, label: '15% off' },
    { value: 25000, label: '20% off' },
    { value: 35000, label: '30% off' },
  ];

  // Representative cart total for the editor preview (mid-ladder: tier 1 reached,
  // so the preview shows both the unlocked and the locked marker treatment).
  static sampleTotal = 12000;

  // Breathing room between a label and its neighbour, and between a threshold and
  // the card edge.
  static labelGutter = 8;

  // Room kept under the lowest label so a wrapped one doesn't sit on the card's edge.
  static labelBottomGap = 12;

  static storageKey = 'sd-promo-tracker-collapsed';

  static dockedCollapsedKey = 'sd-promo-tracker-docked-collapsed';

  static minimizedKey = 'sd-promo-tracker-minimized';
}

customElements.define('sd-promotion-tracker', SdPromotionTracker);
